import type { MeterSpec, SubdivisionSpec } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import type { Stab } from './stab-scheduler.js'

/**
 * Metric click-loop audition primitive. Pure function — emits a chronologically
 * ordered list of `Stab`s for the requested meter, optional subdivision spec,
 * tempo, and number of measures. The Rhythm Trainer's playback hook routes
 * the result through the existing `useStabScheduler` + `usePlayNotes` path.
 *
 * Accent semantics (Bowie §8 pitch-coded secondary accent fix):
 *  - PRIMARY (beat 1):      C6, velocity 1.00
 *  - SECONDARY (other accents in `accentPositions`): A5, velocity 0.90
 *  - UNACCENTED beat:       G5, velocity 0.75
 *  - SUBDIVISION fill:      G5, velocity 0.55
 *
 * Three pitch tiers (C6 / A5 / G5) — m3 below C6 + M2 above G5, both consonant
 * intervals matching the Roland/Korg 3-tier metronome convention. Pitch-coding
 * the secondary accent makes the 6/8 (accent on position 3) vs 3/4 (no
 * secondary accent) perceptual distinction unambiguous; velocity-only
 * differentiation (≈1 dB) was too subtle to discriminate the pair.
 *
 * Duration is fixed at `'32n'` for a staccato envelope (~94 ms at 90 BPM).
 *
 * Two layout modes:
 *  - Beat-only meter audition (Meter ID sub-mode): no `subdivision` passed.
 *    Quarter-note-bottom meters (4/4, 3/4, 5/4) additionally emit a half-
 *    velocity 8th-note fill between beats for grouping audibility. Eighth-
 *    note-bottom meters (6/8, 7/8) skip the fill — the top-number unit IS
 *    an 8th, and accent placement carries the meter.
 *  - Subdivision audition (Subdivision ID sub-mode): meter is 4/4 (caller's
 *    responsibility) and a `SubdivisionSpec` controls the per-beat fill.
 *
 * Spec: docs/superpowers/specs/2026-05-22-rhythm-trainer-v1-design.md §5
 */

// NoteName is the bare pitch-class type; the audio engine consumes the
// `${pitch}${octave}` form via `as NoteName` (the same cast pattern used by
// ear-training interval/quality playback hooks). Cast at the constants so
// the rest of this module stays cast-free.
const PRIMARY_PITCH = 'C6' as NoteName
const SECONDARY_PITCH = 'A5' as NoteName
const WEAK_PITCH = 'G5' as NoteName

const PRIMARY_VEL = 1.0
const SECONDARY_VEL = 0.9
const UNACCENTED_VEL = 0.75
const FILL_VEL = 0.55

const CLICK_DURATION = '32n' as const

export interface ClickEngineInput {
  meter: MeterSpec
  subdivision?: SubdivisionSpec
  /** BPM — Rhythm Trainer v1 locks this at 90. */
  bpm: number
  /** Number of measures to render. Defaults to 4. */
  measures?: number
}

interface PendingStab {
  delayMs: number
  velocity: number
  pitch: NoteName
}

const toStab = (p: PendingStab): Stab => ({
  events: [{ type: 'note' as const, note: p.pitch, duration: CLICK_DURATION, velocity: p.velocity }],
  delayMs: p.delayMs,
})

const beatVelocityFor = (position: number, accentPositions: readonly number[]): number => {
  if (position === 0) {
    return PRIMARY_VEL
  }
  if (accentPositions.includes(position)) {
    return SECONDARY_VEL
  }
  return UNACCENTED_VEL
}

const beatPitchFor = (position: number, accentPositions: readonly number[]): NoteName => {
  if (position === 0) {
    return PRIMARY_PITCH
  }
  if (accentPositions.includes(position)) {
    return SECONDARY_PITCH
  }
  return WEAK_PITCH
}

const buildSubdivisionFills = (
  beatStartMs: number,
  beatMs: number,
  spec: SubdivisionSpec
): PendingStab[] => {
  const fills: PendingStab[] = []
  switch (spec.kind) {
    case '8ths':
      fills.push({ delayMs: beatStartMs + beatMs / 2, velocity: FILL_VEL, pitch: WEAK_PITCH })
      break
    case '16ths':
      for (const frac of [1 / 4, 1 / 2, 3 / 4]) {
        fills.push({ delayMs: beatStartMs + beatMs * frac, velocity: FILL_VEL, pitch: WEAK_PITCH })
      }
      break
    case 'triplets':
      for (const frac of [1 / 3, 2 / 3]) {
        fills.push({ delayMs: beatStartMs + beatMs * frac, velocity: FILL_VEL, pitch: WEAK_PITCH })
      }
      break
    case 'swing':
      // Off-beat placed at 2/3 of the beat → long-short 2:1 ratio against the
      // remaining 1/3. Hard swing at 90 BPM is audibly distinct from straight
      // 8ths (where the off-beat is at 1/2) and from triplets (which carry
      // three even articulations rather than two uneven ones).
      fills.push({ delayMs: beatStartMs + (2 / 3) * beatMs, velocity: FILL_VEL, pitch: WEAK_PITCH })
      break
  }
  return fills
}

export const buildClickStabs = (input: ClickEngineInput): Stab[] => {
  const { meter, subdivision, bpm } = input
  const measures = input.measures ?? 4
  const quarterMs = 60000 / bpm
  const eighthMs = quarterMs / 2
  // bottom=4 meters use a quarter-note per top-number unit; bottom=8 uses an
  // eighth-note per top-number unit. The 6/8 vs 3/4 discriminator emerges
  // from accentPositions (not from beatMs).
  const beatMs = meter.bottomNumber === 8 ? eighthMs : quarterMs
  const measureMs = meter.topNumber * beatMs

  const pending: PendingStab[] = []

  for (let m = 0; m < measures; m++) {
    const measureStart = m * measureMs
    for (let i = 0; i < meter.topNumber; i++) {
      const beatStart = measureStart + i * beatMs
      pending.push({
        delayMs: beatStart,
        velocity: beatVelocityFor(i, meter.accentPositions),
        pitch: beatPitchFor(i, meter.accentPositions),
      })

      if (subdivision) {
        // Subdivision ID audition — apply the subdivision spec at each beat.
        pending.push(...buildSubdivisionFills(beatStart, beatMs, subdivision))
      } else if (meter.bottomNumber === 4) {
        // Beat-only meter audition for quarter-note-bottom meters — fill the
        // off-beat with an 8th-note click so the listener hears the grouping.
        pending.push({ delayMs: beatStart + beatMs / 2, velocity: FILL_VEL, pitch: WEAK_PITCH })
      }
      // bottom=8 meters (6/8, 7/8) skip the fill — the top-number unit is
      // already an 8th. Accent placement carries the meter.
    }
  }

  return pending.sort((a, b) => a.delayMs - b.delayMs).map(toStab)
}
